import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title, Badge, Group, Modal, Stack, Paper, ActionIcon, TextInput, Textarea, Code, Text, Select, Divider, Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconEdit, IconTrash, IconKey, IconDots, IconCheck, IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { groupsApi, permissionsApi } from '../../api/client';
import { PageContainer } from '../../components/PageContainer';
import { Group as GroupType } from '../../types/group';
import { PaginatedList } from '../../components/List';
import { useQueryClient, useQuery } from '@tanstack/react-query';

interface PermissionDef {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
}

export function AdminGroupsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<GroupType | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [newPermission, setNewPermission] = useState<string | null>(null);
  const [customPermission, setCustomPermission] = useState('');

  const { data: allPermissions = [] } = useQuery<PermissionDef[]>({
    queryKey: ['permissions'],
    queryFn: async () => {
      const res = await permissionsApi.list();
      return res.data.data || [];
    },
  });

  const refreshGroups = () => {
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const selectGroupForPermissions = async (group: GroupType) => {
    setSelectedGroup(group);
    try {
      const res = await groupsApi.getPermissions(group.id);
      setPermissions(res.data.data?.permissions || []);
      setShowPermissionsModal(true);
    } catch (err) {
      console.error('Failed to load permissions:', err);
    }
  };

  const handleDelete = async (group: GroupType) => {
    if (group.is_system) {
      notifications.show({ title: t('common.error'), message: t('groups.cannot_delete_system'), color: 'red' });
      return;
    }
    if (!confirm(t('groups.delete_confirm', { name: group.name }))) return;
    try {
      await groupsApi.delete(group.id);
      refreshGroups();
      notifications.show({ title: t('common.success'), message: t('groups.deleted_success'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('groups.delete_failed'), color: 'red' });
    }
  };

  const handleAddPermission = async (permName?: string) => {
    const perm = permName || newPermission || customPermission.trim();
    if (!selectedGroup || !perm) return;
    try {
      await groupsApi.addPermission(selectedGroup.id, perm);
      const res = await groupsApi.getPermissions(selectedGroup.id);
      setPermissions(res.data.data?.permissions || []);
      setNewPermission(null);
      setCustomPermission('');
      notifications.show({ title: t('common.success'), message: t('groups.permission_added'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('groups.permission_add_failed'), color: 'red' });
    }
  };

  const handleRemovePermission = async (permission: string) => {
    if (!selectedGroup) return;
    try {
      await groupsApi.removePermission(selectedGroup.id, permission);
      const res = await groupsApi.getPermissions(selectedGroup.id);
      setPermissions(res.data.data?.permissions || []);
      notifications.show({ title: t('common.success'), message: t('groups.permission_removed'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('groups.permission_remove_failed'), color: 'red' });
    }
  };

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate(-1)}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={2}>{t('groups.title')}</Title>
          </Group>
          <ActionIcon variant="filled" color="blue" size="lg" onClick={() => setShowCreateModal(true)} title={t('groups.create_group')}>
            <IconPlus size={20} />
          </ActionIcon>
        </Group>

        <TextInput
          placeholder={t('groups.search_placeholder') || 'Поиск по названию...'}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <PaginatedList<GroupType>
          queryKey={['groups', { search }]}
          fetchFn={async () => {
            const res = await groupsApi.list();
            let data = res.data.data || [];
            if (!Array.isArray(data)) data = [];
            if (search) {
              const s = search.toLowerCase();
              data = data.filter((g: GroupType) => 
                g.name?.toLowerCase().includes(s) || 
                g.description?.toLowerCase().includes(s)
              );
            }
            return { data, total: data.length };
          }}
          renderItem={(group) => (
            <GroupCard
              group={group}
              onEdit={(g) => { setSelectedGroup(g); setShowEditModal(true); }}
              onDelete={handleDelete}
              onPermissions={selectGroupForPermissions}
            />
          )}
          getItemKey={(group) => group.id}
          emptyMessage={t('groups.not_found')}
          pageSize={20}
          showPageInfo
          showPageSizeSelector
        />
      </Stack>

      <Modal opened={showCreateModal} onClose={() => setShowCreateModal(false)} title={t('groups.create_group')}>
        <CreateGroupForm onSuccess={() => { refreshGroups(); setShowCreateModal(false); }} />
      </Modal>

      <Modal opened={showEditModal} onClose={() => setShowEditModal(false)} title={t('groups.edit_group')}>
        {selectedGroup && <EditGroupForm group={selectedGroup} onSuccess={() => { refreshGroups(); setShowEditModal(false); }} />}
      </Modal>

      <Modal opened={showPermissionsModal} onClose={() => setShowPermissionsModal(false)} title={`${t('groups.permissions')}: ${selectedGroup?.name}`} size="lg">
        <Stack>
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{t('groups.permissions_description')}</Text>
          
          <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>{t('groups.current_permissions')}</Text>
          {permissions.length === 0 ? (
            <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{t('groups.no_permissions')}</Text>
          ) : (
            permissions.map(perm => (
              <Group key={perm} justify="space-between">
                <Code>{perm}</Code>
                <ActionIcon variant="subtle" color="red" onClick={() => handleRemovePermission(perm)}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            ))
          )}

          <Divider my="sm" />
          <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>{t('groups.add_permission')}</Text>
          
          <Select
            placeholder={t('groups.select_permission')}
            data={allPermissions
              .filter(p => !permissions.includes(p.name))
              .map(p => ({ value: p.name, label: `${p.name} - ${p.description}` }))}
            value={newPermission}
            onChange={(val) => { if (val) handleAddPermission(val); }}
            searchable
            clearable
          />

          <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>{t('groups.or_custom')}</Text>
          <Group>
            <TextInput
              placeholder={t('groups.permission_placeholder')}
              value={customPermission}
              onChange={(e) => setCustomPermission(e.target.value)}
              style={{ flex: 1 }}
            />
            <ActionIcon variant="filled" color="blue" size="lg" onClick={() => handleAddPermission()} disabled={!customPermission.trim()} title={t('common.add')}>
              <IconPlus size={20} />
            </ActionIcon>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
}

interface GroupCardProps {
  group: GroupType;
  onEdit: (group: GroupType) => void;
  onDelete: (group: GroupType) => void;
  onPermissions: (group: GroupType) => void;
}

function GroupCard({ group, onEdit, onDelete, onPermissions }: GroupCardProps) {
  const { t } = useTranslation();

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} style={{ fontSize: 'var(--font-size-sm)' }}>{group.name}</Text>
          <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{group.description || '-'}</Text>
          {group.is_system && <Badge color="purple" style={{ alignSelf: 'flex-start', padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>{t('groups.system')}</Badge>}
        </Stack>
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm">
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconKey size={14} />} onClick={() => onPermissions(group)}>
              {t('groups.permissions')}
            </Menu.Item>
            {!group.is_system && (
              <>
                <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEdit(group)}>
                  {t('common.edit')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(group)}>
                  {t('common.delete')}
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Paper>
  );
}

function CreateGroupForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await groupsApi.create(form);
      notifications.show({ title: t('common.success'), message: t('groups.created_success'), color: 'green' });
      onSuccess();
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('groups.create_failed'), color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput label={t('groups.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Textarea label={t('groups.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Group justify="flex-end">
          <ActionIcon type="submit" variant="filled" color="green" size="lg" loading={loading} title={t('common.create')}>
            <IconCheck size={20} />
          </ActionIcon>
        </Group>
      </Stack>
    </form>
  );
}

function EditGroupForm({ group, onSuccess }: { group: GroupType; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: group.name, description: group.description });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await groupsApi.update(group.id, form);
      notifications.show({ title: t('common.success'), message: t('groups.updated_success'), color: 'green' });
      onSuccess();
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('groups.update_failed'), color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput label={t('groups.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Textarea label={t('groups.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Group justify="flex-end">
          <ActionIcon type="submit" variant="filled" color="green" size="lg" loading={loading} title={t('common.save')}>
            <IconCheck size={20} />
          </ActionIcon>
        </Group>
      </Stack>
    </form>
  );
}

export default AdminGroupsPage;
